import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { incidentalLoanService } from '../../services/incidentalLoanService'
import Swal from 'sweetalert2'

function IncidentalLoanDetail() {
  const { NoPinjaman } = useParams()
  const navigate = useNavigate()
  const [loan, setLoan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const [showTopupModal, setShowTopupModal] = useState(false)
  const [selectedAngsuran, setSelectedAngsuran] = useState(null)
  const [jumlahBayar, setJumlahBayar] = useState(0)
  const [catatan, setCatatan] = useState('')
  const [settings, setSettings] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [jadwal, setJadwal] = useState([])

  useEffect(() => {
    fetchLoanDetails()
    fetchSettings()
  }, [NoPinjaman])

  const fetchLoanDetails = async () => {
    try {
      setLoading(true)
      const response = await incidentalLoanService.getById(NoPinjaman)
      setLoan(response.data.data.loan)
      setTransactions(response.data.data.transactions || [])
      setJadwal(response.data.data.jadwal?.jadwal || [])
    } catch (error) {
      console.error('Error fetching loan details:', error)
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: 'Gagal memuat detail pinjaman'
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchSettings = async () => {
    try {
      const response = await incidentalLoanService.getSettings()
      if (response.data.data && response.data.data.length > 0) {
        setSettings(response.data.data[0])
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
    }
  }

  const handleApprove = async () => {
    const result = await Swal.fire({
      title: 'Konfirmasi',
      text: 'Apakah Anda yakin ingin menyetujui pinjaman ini?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ya, Setujui',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    })

    if (!result.isConfirmed) return

    setProcessing(true)
    try {
      await incidentalLoanService.approve(NoPinjaman)
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Pinjaman disetujui'
      })
      fetchLoanDetails()
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.response?.data?.message || 'Gagal menyetujui pinjaman'
      })
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    const { value: alasan } = await Swal.fire({
      title: 'Alasan Penolakan',
      input: 'textarea',
      inputLabel: 'Masukkan alasan penolakan:',
      inputPlaceholder: 'Alasan penolakan...',
      showCancelButton: true,
      confirmButtonText: 'Tolak',
      cancelButtonText: 'Batal'
    })

    if (!alasan) return

    setProcessing(true)
    try {
      await incidentalLoanService.reject(NoPinjaman, alasan)
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Pinjaman ditolak'
      })
      fetchLoanDetails()
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.response?.data?.message || 'Gagal menolak pinjaman'
      })
    } finally {
      setProcessing(false)
    }
  }

  const handlePay = async () => {
    if (!jumlahBayar || parseFloat(jumlahBayar) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validasi',
        text: 'Masukkan jumlah pembayaran yang valid'
      })
      return
    }

    setProcessing(true)
    try {
      await incidentalLoanService.pay(NoPinjaman, parseFloat(jumlahBayar), catatan)
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Pembayaran berhasil'
      })
      setShowPayModal(false)
      setJumlahBayar(0)
      setCatatan('')
      fetchLoanDetails()
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.response?.data?.message || 'Gagal melakukan pembayaran'
      })
    } finally {
      setProcessing(false)
    }
  }

  const handleTopup = async () => {
    if (!jumlahBayar || parseFloat(jumlahBayar) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validasi',
        text: 'Masukkan jumlah topup yang valid'
      })
      return
    }

    if (settings && parseFloat(jumlahBayar) > (settings.Plafon - loan.SisaPokok)) {
      Swal.fire({
        icon: 'warning',
        title: 'Validasi',
        text: `Topup maksimal adalah ${formatCurrency(settings.Plafon - loan.SisaPokok)}`
      })
      return
    }

    setProcessing(true)
    try {
      await incidentalLoanService.topup(NoPinjaman, parseFloat(jumlahBayar), catatan)
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Topup berhasil'
      })
      setShowTopupModal(false)
      setJumlahBayar(0)
      setCatatan('')
      fetchLoanDetails()
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.response?.data?.message || 'Gagal melakukan topup'
      })
    } finally {
      setProcessing(false)
    }
  }

  const openPayModal = () => {
    setJumlahBayar(loan.SisaPokok)
    setCatatan('')
    setShowPayModal(true)
  }

  const openTopupModal = () => {
    setJumlahBayar(settings.Plafon - loan.SisaPokok)
    setCatatan('')
    setShowTopupModal(true)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID')
  }

  const getStatusBadge = (status) => {
    const badges = {
      'Pengajuan': 'badge-warning',
      'Disetujui': 'badge-info',
      'Dicairkan': 'badge-primary',
      'Lunas': 'badge-success',
      'Ditolak': 'badge-danger'
    }
    return badges[status] || 'badge-secondary'
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x"></i>
        <p className="mt-2">Memuat data...</p>
      </div>
    )
  }

  if (!loan) {
    return (
      <div className="alert alert-warning">
        Pinjaman tidak ditemukan
      </div>
    )
  }

  const terbayarPercent = loan.JangkaWaktu > 0 
    ? Math.round(((loan.JangkaWaktu - loan.SisaTenor) / loan.JangkaWaktu) * 100) 
    : 0

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Detail Pinjaman Insidentil</h3>
          <div className="card-tools">
            {loan.Status === 'Pengajuan' && (
              <>
                <button
                  className="btn btn-success btn-sm mr-2"
                  onClick={handleApprove}
                  disabled={processing}
                >
                  <i className="fas fa-check mr-1"></i> Setujui
                </button>
                <button
                  className="btn btn-danger btn-sm mr-2"
                  onClick={handleReject}
                  disabled={processing}
                >
                  <i className="fas fa-times mr-1"></i> Tolak
                </button>
              </>
            )}
            
            <button
              className="btn btn-default btn-sm"
              onClick={() => navigate('/incidental-loans')}
            >
              <i className="fas fa-arrow-left mr-1"></i> Kembali
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td width="150"><strong>No. Pinjaman</strong></td>
                    <td>: {loan.NoPinjaman}</td>
                  </tr>
                  <tr>
                    <td><strong>NBA</strong></td>
                    <td>: {loan.NBA}</td>
                  </tr>
                  <tr>
                    <td><strong>Nama Anggota</strong></td>
                    <td>: {loan.Nama}</td>
                  </tr>
                  <tr>
                    <td><strong>Status</strong></td>
                    <td>:
                      <span className={`badge ml-1 ${getStatusBadge(loan.Status)}`}>
                        {loan.Status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td width="150"><strong>Jumlah Pinjaman</strong></td>
                    <td>: {formatCurrency(loan.JumlahPinjaman)}</td>
                  </tr>
                  <tr>
                    <td><strong>Bunga</strong></td>
                    <td>: {loan.PersentaseBunga}% / tahun (Efektif)</td>
                  </tr>
                  <tr>
                    <td><strong>Jangka Waktu</strong></td>
                    <td>: {loan.JangkaWaktu} bulan</td>
                  </tr>
                  <tr>
                    <td><strong>Tgl Pengajuan</strong></td>
                    <td>: {formatDate(loan.TglPengajuan)}</td>
                  </tr>
                  <tr>
                    <td><strong>Tgl Pencairan</strong></td>
                    <td>: {formatDate(loan.TglPencairan)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-4">
              <div className="info-box bg-info">
                <span className="info-box-icon"><i className="fas fa-money-bill"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Sisa Pokok</span>
                  <span className="info-box-number">{formatCurrency(loan.SisaPokok)}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-box bg-warning">
                <span className="info-box-icon"><i className="fas fa-calendar"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Sisa Tenor</span>
                  <span className="info-box-number">{loan.SisaTenor} bulan</span>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-box bg-success">
                <span className="info-box-icon"><i className="fas fa-percentage"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Terbayar</span>
                  <span className="info-box-number">{terbayarPercent}%</span>
                </div>
              </div>
            </div>
          </div>

          {loan.Status === 'Disetujui' && (
            <div className="mt-4">
              <div className="alert alert-info">
                <i className="fas fa-info-circle mr-2"></i>
                Pinjaman telah disetujui. Silakan lakukan pencairan melalui menu Pencairan Pinjaman Insidentil.
              </div>
            </div>
          )}

          {loan.Catatan && (
            <div className="mt-3">
              <strong>Catatan:</strong>
              <p>{loan.Catatan}</p>
            </div>
          )}

          {jadwal.length > 0 && (
            <div className="mt-4">
              <h5>Jadwal Angsuran</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="thead-light">
                    <tr>
                      <th>Ke</th>
                      <th>Pokok</th>
                      <th>Bunga</th>
                      <th>Total</th>
                      <th>Sisa Pokok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jadwal.map((item) => (
                      <tr key={item.angsuranKe}>
                        <td className="text-center">{item.angsuranKe}</td>
                        <td className="text-right">{formatCurrency(item.pokok)}</td>
                        <td className="text-right">{formatCurrency(item.bunga)}</td>
                        <td className="text-right">{formatCurrency(item.total)}</td>
                        <td className="text-right">{formatCurrency(item.sisaPokok)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {transactions.length > 0 && (
            <div className="mt-4">
              <h5>Riwayat Transaksi</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="thead-light">
                    <tr>
                      <th>Tanggal</th>
                      <th>Jenis</th>
                      <th>Pokok</th>
                      <th>Bunga</th>
                      <th>Denda</th>
                      <th>Total</th>
                      <th>Sisa Pokok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.Id}>
                        <td>{formatDate(tx.TglTransaksi)}</td>
                        <td>{tx.JenisTransaksi}</td>
                        <td className="text-right">{formatCurrency(tx.JumlahPokok)}</td>
                        <td className="text-right">{formatCurrency(tx.JumlahBunga)}</td>
                        <td className="text-right">{formatCurrency(tx.JumlahDenda)}</td>
                        <td className="text-right">{formatCurrency(tx.TotalJumlah)}</td>
                        <td className="text-right">{formatCurrency(tx.SisaPokokSetelah)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPayModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Pembayaran Pinjaman</h5>
                <button type="button" className="close text-white" onClick={() => setShowPayModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Sisa Pokok</label>
                  <input type="text" className="form-control" value={formatCurrency(loan.SisaPokok)} readOnly />
                </div>
                <div className="form-group">
                  <label>Jumlah Bayar *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={jumlahBayar}
                    onChange={(e) => setJumlahBayar(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="form-group">
                  <label>Catatan</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={catatan}
                    onChange={(e) => setCatatan(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPayModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handlePay}
                  disabled={processing}
                >
                  {processing ? 'Memproses...' : 'Bayar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Topup Modal */}
      {showTopupModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header bg-info">
                <h5 className="modal-title">Topup Pinjaman</h5>
                <button type="button" className="close text-white" onClick={() => setShowTopupModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Sisa Pokok</label>
                  <input type="text" className="form-control" value={formatCurrency(loan.SisaPokok)} readOnly />
                </div>
                <div className="form-group">
                  <label>Jumlah Topup *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={jumlahBayar}
                    onChange={(e) => setJumlahBayar(parseInt(e.target.value) || 0)}
                  />
                  {settings && (
                    <small className="text-muted">
                      Plafon: {formatCurrency(settings.Plafon)} | Sisa: {formatCurrency(settings.Plafon - loan.SisaPokok)}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Catatan</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={catatan}
                    onChange={(e) => setCatatan(e.target.value)}
                  />
                </div>
                <div className="alert alert-info">
                  <small>
                    Setelah topup, sisa tenor akan di-reset ke {settings?.JangkaWaktu} bulan
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTopupModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleTopup}
                  disabled={processing}
                >
                  {processing ? 'Memproses...' : 'Topup'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IncidentalLoanDetail
